/**
 * Proposal Timber Pricing
 * -----------------------
 * One-shot DB price re-sync after applying a timber list update. Mirrors
 * `getPiecePrice` in `StructureDetailModal.tsx` so freshly imported lines
 * (or modified lines whose dimensions changed) get repriced immediately
 * instead of waiting for the structure modal to be opened.
 */

import { db } from './firebase';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import {
  normalizeMoistureContent,
  getTimberMoistureRawFromFirestore,
  findTimberCatalogMatch,
  type TimberItem,
} from './materialsDatabaseService';
import type { TimberListItem } from '../types';
import { recomputeStructureTotals } from './proposalTimberApply';
import { type ProposalLikeWithNodeSets } from './proposalTimberDiff';

const TIMBER_COLLECTION = 'materialsDatabase_timber';

/** One-shot fetch of the timber DB. Use for ad-hoc price syncs. */
export async function fetchTimberDatabaseOnce(): Promise<TimberItem[]> {
  const q = query(collection(db, TIMBER_COLLECTION), orderBy('species'));
  const snap = await getDocs(q);
  return snap.docs.map(d => {
    const data = d.data() as Record<string, unknown>;
    const rawM = getTimberMoistureRawFromFirestore(data);
    const n = normalizeMoistureContent(rawM);
    const moistureContent = n === 'green' ? 'green' : n === 'kiln_dried' ? 'kiln_dried' : undefined;
    return {
      id: d.id,
      species: (data.species as string) || '',
      grade: (data.grade as string) || '',
      dimensions: (data.dimensions as string) || '',
      moistureContent,
      piecePrice: (data.piecePrice as number) || 0,
      costPerBdFt: (data.costPerBdFt as number) || 0,
      boardFeet: (data.boardFeet as number) || 0,
      supplier: (data.supplier as string) || '',
      createdAt: new Date(),
      updatedAt: new Date(),
    } as TimberItem;
  });
}

function getPiecePrice(
  database: TimberItem[],
  species: string,
  grade: string,
  dimensions: string,
  moistureRaw?: string,
): number {
  const match = findTimberCatalogMatch(database, species, grade, dimensions, moistureRaw);
  return match ? match.piecePrice : 0;
}

/**
 * Re-price every line on the given structures from the materials DB. Returns
 * a new ProposalBuilderData. Lines with no DB match keep their current
 * unitCost (so existing manual prices are preserved when possible).
 */
export async function resyncDbPricesForStructures<TState extends ProposalLikeWithNodeSets>(
  state: TState,
  touchedNodeSetIds: string[],
): Promise<TState> {
  if (!touchedNodeSetIds.length) return state;

  let database: TimberItem[];
  try {
    database = await fetchTimberDatabaseOnce();
  } catch (err) {
    // If the DB fetch fails, leave the state untouched; the structure modal
    // will refill prices the next time it opens (existing behavior).
    console.error('[resyncDbPricesForStructures] timber DB fetch failed', err);
    return state;
  }

  const touched = new Set(touchedNodeSetIds);
  const nodeSets = state.nodeSets.map(ns => {
    if (!touched.has(ns.id)) return ns;
    const list: TimberListItem[] = (ns.materials.timberList || []).map(item => {
      const dbPrice = getPiecePrice(database, item.species, item.grade, item.dimensions, item.moistureContent);
      // Only overwrite when:
      //  - the line currently has no price (newly added or dims-changed reset to 0), or
      //  - the DB returns a real price that differs meaningfully from the stored one.
      const currentUnit = Number(item.unitCost) || 0;
      if (currentUnit === 0 && dbPrice > 0) {
        return { ...item, unitCost: dbPrice, total: Math.round(dbPrice * item.quantity * 100) / 100 };
      }
      if (dbPrice > 0 && Math.abs(dbPrice - currentUnit) > 0.001) {
        return { ...item, unitCost: dbPrice, total: Math.round(dbPrice * item.quantity * 100) / 100 };
      }
      // Recompute total in case quantity changed but unit was preserved.
      return { ...item, total: Math.round(currentUnit * item.quantity * 100) / 100 };
    });
    return recomputeStructureTotals({
      ...ns,
      materials: { ...ns.materials, timberList: list },
    });
  });

  return { ...state, nodeSets };
}
